import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";
import {
  createSuggestion,
  getProduct,
  getProductStatus,
  updateProduct,
} from "../api";
import {
  Affix,
  Anchor,
  Breadcrumbs,
  Button,
  Center,
  Code,
  Container,
  Group,
  Loader,
  Switch,
  Table,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconDeviceFloppy, IconWand } from "@tabler/icons-react";
import ProductAttributeRow from "./Details/ProductAttributeRow";
import { isVariantGroupAttribute } from "./Details/variantGroup";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { Link } from "wouter";
import ProductStatusBadge from "./ProductStatusBadge";

const DRAFT_PREFIX = "optivise:product-draft:";
const LEAVE_MESSAGE =
  "You have unsaved changes. Leave this page? Unsaved edits are kept in this browser.";

function draftKey(id) {
  return DRAFT_PREFIX + id;
}

function readDraft(id) {
  try {
    const raw = localStorage.getItem(draftKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDraft(id, values) {
  try {
    localStorage.setItem(
      draftKey(id),
      JSON.stringify({ values, savedAt: Date.now() })
    );
  } catch {
    // quota / private mode — editing still works, just without a draft
  }
}

function clearDraft(id) {
  try {
    localStorage.removeItem(draftKey(id));
  } catch {
    // ignore
  }
}

function valuesEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

function normalizeAttribValue(attrib, value) {
  if (attrib?.type === "LIST_MULTIPLE_VALUES") {
    if (Array.isArray(value)) {
      return value
        .filter((v) => v !== null && v !== undefined && v !== "")
        .map(String);
    }
    if (value === null || value === undefined || value === "") return [];
    return [String(value)];
  }
  if (attrib?.type === "LIST") {
    if (Array.isArray(value))
      return value[0] != null && value[0] !== "" ? String(value[0]) : "";
    if (value === null || value === undefined) return "";
    return String(value);
  }
  return value;
}

function ProductHierarchy({ hierarchy, marketplaceId, skipRoot = true }) {
  return (
    <Breadcrumbs separator="→" separatorMargin="xs" mt="xs">
      {hierarchy.levels.slice(skipRoot ? 1 : 0).map((item) => (
        <Anchor
          component={Link}
          key={item.id}
          to={`/marketplaces/${marketplaceId}/types/${item.id}`}
        >
          {item.label} <Code>{item.code}</Code>
        </Anchor>
      ))}
    </Breadcrumbs>
  );
}

export default function Details({ params }) {
  const [product, setProduct] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isOptivising, setIsOptivising] = useState(false);
  const [allFieldValues, setAllFieldValues] = useState([]);
  const pollingIntervalRef = useRef(null); // Use ref to store interval ID
  const productIdRef = useRef(params.id);
  const skipDraftRef = useRef(false);
  const draftTimerRef = useRef(null);
  const [, setDraftTick] = useState(0);

  useEffect(() => {
    productIdRef.current = params.id;
  }, [params.id]);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {},
    onValuesChange: (values) => {
      if (skipDraftRef.current) return;
      const id = productIdRef.current;
      if (!id) return;
      window.clearTimeout(draftTimerRef.current);
      draftTimerRef.current = window.setTimeout(() => {
        writeDraft(id, values);
      }, 400);
    },
  });

  const isDirty = useCallback(() => form.isDirty(), [form]);
  useUnsavedChanges(isDirty, LEAVE_MESSAGE);

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const setProductAndForm = (data, { restoreDraft = false } = {}) => {
    setProduct(data);
    let allValues = [];

    let initialValues = {};
    for (let i = 0; i < data.hydratedType.attributes.length; i++) {
      const attrib = data.hydratedType.attributes[i];
      const attribValue = data.fieldValues[attrib.code]?.value;
      initialValues[attrib.code] = normalizeAttribValue(attrib, attribValue);
      allValues.push(attrib);
    }
    for (const field in data.fieldValues) {
      const fieldValue = data.fieldValues[field];
      if (initialValues.hasOwnProperty(fieldValue.code)) continue;
      if (fieldValue.retired) {
        initialValues[fieldValue.code] = normalizeAttribValue(
          fieldValue,
          fieldValue.value
        );
        allValues.push({
          code: fieldValue.code,
          label: fieldValue.label || fieldValue.code,
          description: fieldValue.description,
          type: fieldValue.type,
          requirementLevel: fieldValue.requirementLevel,
          retired: true,
        });
        continue;
      }
      initialValues[fieldValue.code] = fieldValue.value;
      allValues.push({
        code: fieldValue.code,
        label: fieldValue.code,
        type: "Undefined",
      });
    }

    skipDraftRef.current = true;
    form.setValues(initialValues);
    form.setInitialValues(initialValues);
    setAllFieldValues(allValues);
    form.setDirty(false);

    if (restoreDraft) {
      const draft = readDraft(data.id);
      if (draft?.values && !valuesEqual(draft.values, initialValues)) {
        form.setValues(draft.values);
        notifications.show({
          title: "Restored unsaved edits",
          message: "This browser still had edits that were not saved.",
        });
        setDraftTick((n) => n + 1);
      }
    }
    skipDraftRef.current = false;
  };

  useEffect(() => {
    getProduct(params.id).then((data) => {
      setProductAndForm(data, { restoreDraft: true });
    });
    return () => {
      window.clearTimeout(draftTimerRef.current);
    };
  }, [params.id]);

  const attributes = useMemo(() => {
    if (!product) return [];
    const filterValue = filter.toLowerCase().trim();
    const shouldFilter = !showAll && filterValue.length > 0;
    const validationFailures = product.validationFailures || [];
    const fieldValues = product.fieldValues || {};

    const base = allFieldValues.filter((attrib) => {
      if (showAll) return true;

      const attribValue = fieldValues[attrib.code]?.value;

      if (shouldFilter) {
        const valueText = Array.isArray(attribValue)
          ? attribValue.join(" ")
          : attribValue != null
            ? String(attribValue)
            : "";
        return (
          attrib.label?.toLowerCase().includes(filterValue) ||
          attrib.code?.toLowerCase().includes(filterValue) ||
          attrib.description?.toLowerCase().includes(filterValue) ||
          valueText.toLowerCase().includes(filterValue)
        );
      }

      const hasValidationFailure = validationFailures.some(
        (p) => p.field === attrib.code
      );
      const hasValue = Array.isArray(attribValue)
        ? attribValue.length > 0
        : Boolean(attribValue);

      return (
        attrib.requirementLevel === "REQUIRED" ||
        hasValue ||
        hasValidationFailure ||
        isVariantGroupAttribute(attrib)
      );
    });

    return base.sort((a, b) => {
      const validationFailuresA = validationFailures.some(
        (p) => p.field === a.code
      );
      const validationFailuresB = validationFailures.some(
        (p) => p.field === b.code
      );
      if (validationFailuresA && !validationFailuresB) return -1;
      if (!validationFailuresA && validationFailuresB) return 1;

      if (a.roles && b.roles && a.roles.length !== b.roles.length)
        return b.roles.length - a.roles.length;
      if (a.hierarchyDepth !== b.hierarchyDepth)
        return a.hierarchyDepth - b.hierarchyDepth;
      return a.code.toLowerCase().localeCompare(b.code.toLowerCase());
    });
  }, [allFieldValues, filter, product, showAll]);

  const formValues = form.getValues() || {};
  const formErrors = form.errors || {};

  const handleDeleteAttribute = useCallback(
    (code) => {
      setAllFieldValues((prev) => prev.filter((a) => a.code !== code));
      form.setDirty({ [code]: true });
    },
    [form]
  );

  if (!product)
    return (
      <Container size="xs">
        <Center>
          <Loader mt="xl" size="xl" />
        </Center>
      </Container>
    );

  const hasOriginalValues =
    product.originalValues &&
    typeof product.originalValues === "object" &&
    Object.values(product.originalValues).some((v) => v !== "" && v != null);

  return (
    <Container size="xl">
      <Group justify="space-between" wrap="nowrap">
        <Anchor
          onClick={() => {
            if (form.isDirty() && !window.confirm(LEAVE_MESSAGE)) return;
            window.history.back();
          }}
          c="gray"
        >
          ← Back
        </Anchor>
        <Group>
          {[...new Set(product.status.split(","))].map((flag) => (
            <ProductStatusBadge
              key={product.id + "status" + flag}
              status={flag.trim()}
            />
          ))}
        </Group>
      </Group>

      <Title order={1}>{product.fieldValues?.name?.value}</Title>

      <ProductHierarchy
        marketplaceId={product.marketplaceId}
        hierarchy={product.hierarchy}
      />

      <div
        style={
          hasOriginalValues
            ? { width: "100%" }
            : { width: "60%", margin: "0 auto" }
        }
      >
        <Group align="flex-end" my="sm" justify="space-between">
          <Group align="flex-start">
            <TextInput
              label="Filter"
              disabled={showAll}
              value={filter}
              onChange={(event) => setFilter(event.currentTarget.value)}
            />
          </Group>
        </Group>

        <form
          onSubmit={form.onSubmit((values) => console.log("Submit", values))}
        >
          <Affix position={{ bottom: 20, right: 20 }}>
            <Group>
              <Button
                type="reset"
                display={form.isDirty() === false ? "none" : "block"}
                disabled={form.isDirty() === false}
                variant="default"
                onClick={() => {
                  form.reset();
                  clearDraft(product.id);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSaving}
                display={form.isDirty() === false ? "none" : "block"}
                disabled={form.isDirty() === false}
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={() => {
                  setIsSaving(true);
                  const formValues = form.getValues();
                  let formValuesStr = {};
                  Object.keys(formValues).forEach((key) => {
                    const value = formValues[key];
                    // Skip null/undefined/empty string values (retain previous behavior of excluding falsy except 0/false)
                    if (value === null || value === undefined || value === "")
                      return;

                    if (Array.isArray(value)) {
                      // Allow arrays of (string|number|boolean) by coercing each element to string and filtering empties
                      const arr = value
                        .filter(
                          (v) => v !== null && v !== undefined && v !== ""
                        )
                        .map((v) => (typeof v === "string" ? v : String(v)));
                      if (arr.length > 0) formValuesStr[key] = arr; // preserve as array
                    } else if (
                      typeof value === "number" ||
                      typeof value === "boolean" ||
                      value instanceof Number ||
                      value instanceof Boolean
                    ) {
                      formValuesStr[key] = String(value);
                    } else if (typeof value === "string") {
                      formValuesStr[key] = value; // already string
                    } else {
                      // Fallback: attempt to stringify other primitives (e.g., Date) while avoiding [object Object]
                      if (
                        value instanceof Date ||
                        typeof value.toString === "function"
                      ) {
                        const str = value.toString();
                        if (str && str !== "[object Object]")
                          formValuesStr[key] = str;
                      }
                    }
                  });

                  updateProduct(product.id, {
                    fieldValues: formValuesStr,
                  })
                    .then((data) => {
                      notifications.show({ title: "Saved" });
                      clearDraft(product.id);
                      setProductAndForm(data);
                    })
                    .finally(() => {
                      setIsSaving(false);
                    });
                }}
              >
                Save
              </Button>

              <Tooltip
                label={
                  product.status.includes("optivised")
                    ? "Already optivised"
                    : "Optivise this product and update all its attributes"
                }
              >
                <Button
                  display={form.isDirty() === true ? "none" : "block"}
                  loading={isOptivising}
                  disabled={product.status.includes("optivised")}
                  leftSection={<IconWand size={16} />}
                  onClick={() => {
                    setIsOptivising(true);
                    setProduct({
                      ...product,
                      status: product.status + ",processing",
                    });
                    createSuggestion(product.id)
                      .then(() => {
                        // Clear any existing interval before starting a new one
                        if (pollingIntervalRef.current) {
                          clearInterval(pollingIntervalRef.current);
                        }
                        // Start polling
                        pollingIntervalRef.current = setInterval(() => {
                          getProductStatus(product.id)
                            .then((statusData) => {
                              const status = statusData.status;
                              // Check if status includes 'failed' or 'optivised' and no longer 'processing'
                              const isProcessing =
                                status.includes("processing");
                              const isFinished =
                                status.includes("failed") ||
                                status.includes("optivised");

                              if (isFinished && !isProcessing) {
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null; // Clear ref

                                getProduct(product.id)
                                  .then((updatedProductData) => {
                                    setProductAndForm(updatedProductData);
                                    setIsOptivising(false);
                                    notifications.show({
                                      title: `Optivisation ${
                                        status.includes("failed")
                                          ? "failed"
                                          : "complete"
                                      }`,
                                    });
                                  })
                                  .catch((error) => {
                                    console.error(
                                      "Failed to fetch product after polling:",
                                      error
                                    );
                                    setIsOptivising(false); // Still stop loading indicator
                                    notifications.show({
                                      title: "Error fetching product",
                                      message: error.message,
                                      color: "red",
                                    });
                                    // Optionally fetch product again to get latest state even on error
                                    getProduct(product.id)
                                      .then(setProductAndForm)
                                      .catch(console.error);
                                  });
                              } else if (!isProcessing) {
                                // If it's not processing anymore but also not failed/optivised (unexpected state)
                                console.warn(
                                  "Polling stopped due to unexpected status:",
                                  status
                                );
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null;
                                // Fetch the latest product data to reflect the current state
                                getProduct(product.id)
                                  .then(setProductAndForm)
                                  .catch(console.error);
                                setIsOptivising(false);
                              }
                              // else: still processing, continue polling
                            })
                            .catch((error) => {
                              console.error(
                                "Error during status polling:",
                                error
                              );
                              // Stop polling on error to avoid infinite loops if endpoint is broken
                              clearInterval(pollingIntervalRef.current);
                              pollingIntervalRef.current = null;
                              setIsOptivising(false);
                              notifications.show({
                                title: "Polling Error",
                                message: "Could not get product status.",
                                color: "red",
                              });
                              // Fetch the latest product data to reflect the current state
                              getProduct(product.id)
                                .then(setProductAndForm)
                                .catch(console.error);
                            });
                        }, 5000); // Poll every 5 seconds
                      })
                      .catch((error) => {
                        console.error("Failed to create suggestion:", error);
                        setIsOptivising(false); // Reset loading state
                        // Fetch product again to revert optimistic status update and get actual status
                        getProduct(product.id)
                          .then(setProductAndForm)
                          .catch(console.error);
                        notifications.show({
                          title: "Optivisation Error",
                          message: error.message,
                          color: "red",
                        });
                      });
                  }}
                >
                  Optivise
                </Button>
              </Tooltip>
            </Group>
          </Affix>

          <Table
            mb={100}
            stickyHeader
            stickyHeaderOffset={58}
            mt="xl"
            variant="vertical"
            withTableBorder
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Group gap="lg">
                    Attribute
                    <Switch
                      label="Show all"
                      checked={showAll}
                      onChange={(event) =>
                        setShowAll(event.currentTarget.checked)
                      }
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={hasOriginalValues ? { width: "40%" } : null}>
                  Current Content
                </Table.Th>
                {hasOriginalValues && (
                  <Table.Th style={{ width: "40%" }}>Original Content</Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {attributes.map((attrib) => (
                <ProductAttributeRow
                  showOriginalValue={hasOriginalValues}
                  form={form}
                  key={attrib.code}
                  product={product}
                  attrib={attrib}
                  fieldValue={formValues?.[attrib.code]}
                  fieldError={formErrors?.[attrib.code]}
                  onDeleteAttribute={handleDeleteAttribute}
                />
              ))}
            </Table.Tbody>
          </Table>
        </form>
      </div>
    </Container>
  );
}
